import { useState, useRef, useEffect, useCallback, useMemo } from "react";
import { useSelector } from "react-redux";
import { motion } from "framer-motion";
import { useInfiniteQuery, useQuery, useQueryClient } from "@tanstack/react-query";
import { useLocation, useNavigate, useParams, useNavigationType } from "react-router-dom";
import useApi from "@/utils/useApi";
import apis from "@/utils/apis";
import ProductCard from "@/components/ProductCard";
import Header from "@/components/Header";
import NoResultsPrompt from "@/components/NoResultsPrompt";
import { useTranslation } from "react-i18next";
import { DEFAULT_CATEGORY_ID } from "@/utils/constants";
import CategoryTab from "./components/CategoryTab";
import PullToRefresh from "@/components/PullToRefresh";
import { useInfiniteScroll } from "@/hooks/useInfiniteScroll";

const ProductsPageV2 = () => {
  const { t } = useTranslation();
  const { categoryId } = useParams();
  const navigate = useNavigate();
  const location = useLocation();
  const navigationType = useNavigationType();
  const user = useSelector((state) => state.user.userInfo);
  const martId = user?.defaultAddress?.nearby?.mart?._id;
  const queryClient = useQueryClient();
  
  // Store initial navigation state - this should not change during component lifecycle
  // Check if user navigated here via browser back/forward button (POP) or clicking link (PUSH)
  const isBackNavigationRef = useRef(navigationType === 'POP');
  const isBackNavigation = isBackNavigationRef.current;

  // On direct visits, reset queries to start fresh from page 1
  useEffect(() => {
    if (!isBackNavigation) {
      queryClient.resetQueries({ 
        queryKey: ['categoryProducts', martId, selectedSubCategory || selectedParentCategory, searchTerm] 
      });
      queryClient.resetQueries({ queryKey: ['subCategories', selectedParentCategory] });
      queryClient.resetQueries({ queryKey: ['parentCategories', DEFAULT_CATEGORY_ID] });
    }
  }, []); // Only run once on mount

  // Try to restore sub-category selection from sessionStorage
  const getInitialSubCategory = () => {
    try {
      const stored = sessionStorage.getItem(`subCategory_${categoryId}`);
      return stored || categoryId;
    } catch {
      return categoryId;
    }
  };

  const [selectedParentCategory, setSelectedParentCategory] =
    useState(categoryId);
  const [selectedSubCategory, setSelectedSubCategory] = useState(
    getInitialSubCategory
  );
  const contentRef = useRef(null);
  const [searchInput, setSearchInput] = useState("");
  const [searchTerm, setSearchTerm] = useState(null);

  // Query hooks
  const { callApi: getAssignedAndProperProducts } = useApi();
  const { callApi: getSubCategories } = useApi();
  const { callApi: getParentCategories } = useApi();
  const { callApi: getCategoryProducts } = useApi();

  // Infinite query for products  
  const {
    data: productsData,
    fetchNextPage,
    hasNextPage,
    isFetchingNextPage,
    isLoading: productsByCategoryLoading,
    isRefetching: isRefetchingProducts,
    refetch,
  } = useInfiniteQuery({
    queryKey: ['categoryProducts', martId, selectedSubCategory || selectedParentCategory, searchTerm],
    queryFn: async ({ pageParam = 1 }) => {
      let param = `${martId}/list/v3/?categoryId=${selectedSubCategory || selectedParentCategory}&page=${pageParam}`;
      if (searchTerm?.trim()) {
        param += `&search=${searchTerm}`;
      }

      const res = await getCategoryProducts({
        api: apis.getProductByCategory,
        param,
      });
      return {
        products: res?.products || [],
        currentPage: pageParam,
        totalPages: res?.totalPages || 1,
      };
    },
    getNextPageParam: (lastPage) => {
      if (lastPage.currentPage < lastPage.totalPages) {
        return lastPage.currentPage + 1;
      }
      return undefined;
    },
    enabled: !!martId && !!(selectedSubCategory || selectedParentCategory),
    initialPageParam: 1,
    staleTime: isBackNavigation ? Infinity : 0,
    gcTime: 10 * 60 * 1000,
    refetchOnMount: false, // Don't refetch - we reset the query on direct visits
    refetchOnWindowFocus: false,
    refetchOnReconnect: false,
  });

  // DEBUG: Log products query config
  useEffect(() => {
    console.debug('📦 ProductsPage Query Config:', {
      enabled: !!martId && !!(selectedSubCategory || selectedParentCategory),
      staleTime: isBackNavigation ? Infinity : 0,
      refetchOnMount: isBackNavigation ? false : 'always',
      isBackNavigation,
      queryKey: ['categoryProducts', martId, selectedSubCategory || selectedParentCategory, searchTerm],
    });
  }, [selectedSubCategory, selectedParentCategory, searchTerm]);

  // Flatten products from all pages
  const data = productsData?.pages?.flatMap(page => page.products) || [];
  const totalPages = productsData?.pages?.[0]?.totalPages || 1;
  const page = productsData?.pages?.length || 1;
  const isLoading = productsByCategoryLoading;

  const isDefaultParentCategory = selectedParentCategory === DEFAULT_CATEGORY_ID;

  // Fetch sub-categories
  const { data: subCategories, isLoading: isSubCategoryLoading } = useQuery({
    queryKey: ['subCategories', selectedParentCategory],
    queryFn: async () => {
      return await getSubCategories({
        api: apis.listCategory,
        query: { parentCategory: selectedParentCategory },
      });
    },
    enabled: !!selectedParentCategory && !isDefaultParentCategory,
    staleTime: isBackNavigation ? Infinity : 0,
    gcTime: 10 * 60 * 1000,
    refetchOnMount: false, // Don't refetch - we reset the query on direct visits
    refetchOnWindowFocus: false,
    refetchOnReconnect: false,
  });

  // Fetch parent categories
  const { data: parentCategories, isLoading: isParentCategoryLoading } = useQuery({
    queryKey: ['parentCategories', DEFAULT_CATEGORY_ID],
    queryFn: async () => {
      return await getParentCategories({
        api: apis.listCategory,
        query: { parentCategory: DEFAULT_CATEGORY_ID },
      });
    },
    staleTime: isBackNavigation ? Infinity : 0,
    gcTime: 10 * 60 * 1000,
    refetchOnMount: false, // Don't refetch - we reset the query on direct visits
    refetchOnWindowFocus: false,
    refetchOnReconnect: false,
  });

  useEffect(() => {
    // Initialize selectedParentCategory from URL params
    setSelectedParentCategory(categoryId);
  }, [categoryId]);

  const handleSubCategorySelect = (categoryId) => {
    setSelectedSubCategory(categoryId);
    // Persist sub-category selection
    try {
      sessionStorage.setItem(`subCategory_${selectedParentCategory}`, categoryId);
    } catch (e) {
      // Ignore storage errors
    }
  };

  const handleParentCategorySelect = (parentCategoryId) => {
    // Update URL parameter without navigation/history
    const newUrl = `/products/v2/${parentCategoryId}`;
    window.history.replaceState(null, "", newUrl);

    // Always update the parent category
    setSelectedParentCategory(parentCategoryId);
    
    // Always update subcategory when parent changes
    try {
      const stored = sessionStorage.getItem(`subCategory_${parentCategoryId}`);
      setSelectedSubCategory(stored || parentCategoryId);
    } catch {
      setSelectedSubCategory(parentCategoryId);
    }
    
    // Scroll to top when category changes
    window.scrollTo({ top: 0, behavior: 'smooth' });
  };

  const handleSearch = (value) => {
    setSearchInput(value);
  };

  // Debounce search input
  useEffect(() => {
    const trimmedQuery = searchInput?.trim();
    
    if (!trimmedQuery) {
      setSearchTerm(null);
      // Scroll to top when search is cleared
      window.scrollTo({ top: 0, behavior: 'smooth' });
      return;
    }

    const searchTimeout = setTimeout(() => {
      setSearchTerm(trimmedQuery);
      // Scroll to top when search is executed
      window.scrollTo({ top: 0, behavior: 'smooth' });
    }, 500);

    return () => clearTimeout(searchTimeout);
  }, [searchInput]);

  // Infinite scroll
  const handleLoadMore = useCallback(() => {
    if (hasNextPage && !isFetchingNextPage) {
      fetchNextPage();
    }
  }, [hasNextPage, isFetchingNextPage, fetchNextPage]);

  const sentinelRef = useInfiniteScroll(handleLoadMore, hasNextPage, isFetchingNextPage);

  // Handle refresh
  const handleRefresh = useCallback(async () => {
    await queryClient.resetQueries({ queryKey: ['categoryProducts', martId, selectedSubCategory || selectedParentCategory, searchTerm] });
  }, [queryClient, martId, selectedSubCategory, selectedParentCategory, searchTerm]);

  const getCategoryName = () => {
    if (!selectedParentCategory || !parentCategories) return "";
    const category = parentCategories.find(
      (cat) => cat._id === selectedParentCategory
    );
    return category ? category.name : "";
  };

  return (
    <div className="font-sans min-h-screen">
      <Header
        searchQuery={searchInput}
        setSearchQuery={handleSearch}
        categoryName={getCategoryName()}
      />

      <PullToRefresh containerRef={contentRef} onRefresh={handleRefresh}>
        <main className="p-4" ref={contentRef}>
          <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }}>
            <div>
              {/* Parent Categories Section */}
              <div className="mb-4">
                <h3 className="text-lg font-semibold text-gray-900 dark:text-gray-100 mb-3">
                  Categories
                </h3>
                <div className="flex gap-3 overflow-x-auto scrollbar-hide pb-2">
                  <div
                    className={`bg-white dark:bg-gray-800 rounded-xl shadow-sm border cursor-pointer min-w-[120px] flex-shrink-0 flex justify-center items-center ${
                      !selectedParentCategory || isDefaultParentCategory
                        ? "border-blue-700 ring-2 ring-blue-200 dark:ring-blue-800"
                        : "border-gray-100 dark:border-gray-700"
                    } p-2 transition-all duration-200`}
                    onClick={() =>
                      handleParentCategorySelect(DEFAULT_CATEGORY_ID)
                    }
                  >
                    <div className="flex items-center justify-center">
                      <h3 className="font-medium text-gray-800 dark:text-gray-200 text-sm">
                        All
                      </h3>
                    </div>
                  </div>

                  {isParentCategoryLoading
                    ? Array.from({ length: 6 }).map((_, index) => (
                        <div
                          key={index}
                          className="bg-white dark:bg-gray-800 rounded-xl shadow-sm border border-gray-100 dark:border-gray-700 p-2 animate-pulse min-w-[180px] flex-shrink-0"
                        >
                          <div className="flex items-center gap-3">
                            <div className="w-12 h-12 bg-gray-300 dark:bg-gray-600 rounded-lg"></div>
                            <div className="flex-1">
                              <div className="h-4 bg-gray-300 dark:bg-gray-600 rounded w-3/4 mb-1"></div>
                            </div>
                          </div>
                        </div>
                      ))
                    : parentCategories?.map((category, idx) => (
                        <CategoryTab
                          key={idx}
                          category={category}
                          isSelected={selectedParentCategory === category._id}
                          onClick={() =>
                            handleParentCategorySelect(category._id)
                          }
                        />
                      ))}
                </div>
              </div>

              {/* Subcategories Section - only show when a parent category is selected AND has subcategories */}
              {selectedParentCategory &&
                !isDefaultParentCategory &&
                subCategories &&
                subCategories.length > 0 &&
                !searchTerm && (
                  <div>
                    <h3 className="text-md font-medium text-gray-700 dark:text-gray-300 mb-3">
                      Subcategories
                    </h3>
                    <div className="flex gap-3 overflow-x-auto scrollbar-hide pb-2">
                      {/* All Subcategories Option */}
                      <div
                        className={`bg-white dark:bg-gray-800 rounded-xl shadow-sm border cursor-pointer min-w-[120px] flex-shrink-0 flex justify-center items-center ${
                          selectedSubCategory === selectedParentCategory
                            ? "border-blue-700 ring-2 ring-blue-200 dark:ring-blue-800"
                            : "border-gray-100 dark:border-gray-700"
                        } p-2 transition-all duration-200`}
                        onClick={() => {
                          handleSubCategorySelect(selectedParentCategory);
                        }}
                      >
                        <div className="flex items-center justify-center">
                          <h3 className="font-medium text-gray-800 dark:text-gray-200 text-sm">
                            All
                          </h3>
                        </div>
                      </div>

                      {isSubCategoryLoading
                        ? Array.from({ length: 6 }).map((_, index) => (
                            <div
                              key={index}
                              className="bg-white dark:bg-gray-800 rounded-xl shadow-sm border border-gray-100 dark:border-gray-700 p-2 animate-pulse min-w-[180px] flex-shrink-0"
                            >
                              <div className="flex items-center gap-3">
                                <div className="w-12 h-12 bg-gray-300 dark:bg-gray-600 rounded-lg"></div>
                                <div className="flex-1">
                                  <div className="h-4 bg-gray-300 dark:bg-gray-600 rounded w-3/4 mb-1"></div>
                                </div>
                              </div>
                            </div>
                          ))
                        : subCategories?.map((category) => (
                            <CategoryTab
                              key={category._id}
                              category={category}
                              isSelected={selectedSubCategory === category._id}
                              onClick={() =>
                                handleSubCategorySelect(category._id)
                              }
                            />
                          ))}
                    </div>
                  </div>
                )}
            </div>
          </motion.div>

          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 lg:grid-cols-5 gap-4 mt-4"
          >
            {/* Show skeleton loaders on initial load or refetch on direct visit */}
            {(isLoading || (isRefetchingProducts && !isBackNavigation))
              ? Array.from({ length: 10 }).map((_, index) => (
                  <ProductCard key={`loading-initial-${index}`} loading={true} />
                ))
              : data?.length > 0
              ? data.map((product, index) => (
                  <ProductCard key={product?.stockEntries?.[0]?._id || product?.variants?.[0]?._id || `${product?.name}_${index}`} data={product} />
                ))
              : data?.length === 0 &&
                !isLoading && (
                  <div className="col-span-full">
                    <NoResultsPrompt searchText={searchTerm} />
                  </div>
                )}

            {/* Show skeleton loaders at bottom when loading more products */}
            {isFetchingNextPage && (
              <>
                {Array.from({ length: 5 }).map((_, index) => (
                  <ProductCard key={`loading-${index}`} loading={true} />
                ))}
              </>
            )}

            {/* Sentinel element for infinite scroll */}
            {data?.length > 0 && hasNextPage && !isFetchingNextPage && (
              <div ref={sentinelRef} className="col-span-full h-10" />
            )}
          </motion.div>
        </main>
      </PullToRefresh>
    </div>
  );
};

export default ProductsPageV2;
